import DOMPurify, { type Config, type UponSanitizeAttributeHook, type NodeHook } from "dompurify";

/**
 * 客服 / 自动回复富文本（后台 wangEditor 产出）的净化规则。
 * main.ts 的 v-dompurify-html 指令与 dealTextCon 共用，指令是最终防线，两处必须一致。
 */
export const RICH_TEXT_CONFIG: Config = {
	ALLOWED_TAGS: [
		// 段落 / 换行 / 文字样式
		"p", "br", "span", "div", "strong", "b", "em", "i", "u", "s", "del", "strike", "sub", "sup", "code", "pre",
		// 标题 / 引用 / 列表 / 分割线
		"h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "ul", "ol", "li", "hr",
		// 链接 / 图片 / 视频
		"a", "img", "video", "source", "iframe",
		// 表格
		"table", "colgroup", "col", "thead", "tbody", "tfoot", "tr", "th", "td",
		// 待办（wangEditor todo：<input type="checkbox" disabled>）
		"input",
	],
	ALLOWED_ATTR: [
		"href", "target", "rel", "style", "class", "src", "alt", "title",
		"width", "height", "colspan", "rowspan", "poster", "controls", "type", "checked", "disabled",
		"allowfullscreen", "frameborder",
	],
};

// 只放行排版类 CSS；position / z-index / margin / transform 等可做“全屏遮罩钓鱼”的属性一律丢弃
const ALLOWED_STYLE_PROP =
	/^(color|background-color|font-(size|family|weight|style)|text-(align|indent)|text-decoration(-[a-z]+)?|line-height|letter-spacing|vertical-align|white-space|(max-|min-)?(width|height)|border(-[a-z-]+)?|padding(-[a-z]+)?|list-style-type|table-layout)$/;
const UNSAFE_STYLE_VALUE = /url\s*\(|expression|javascript:|@import|[<>\\]/i;
// 视口单位：正常消息不会用，伪造全屏色块会用（100vw / 100vh / 100dvh ...）
const VIEWPORT_UNIT = /\d\s*(vw|vh|vmin|vmax|[dsl]v[hwib]|v[ib])\b/i;
// 宽高只允许媒体和表格写（编辑器只给图片、表格写尺寸）；其它元素写 width:1920px;height:1080px 只会撑出一块远超气泡的色块
const SIZE_PROP = /^(max-|min-)?(width|height)$/;
const SIZED_TAGS = new Set(["IMG", "VIDEO", "IFRAME", "TABLE", "COL", "TH", "TD"]);

// class 只保留代码块语言名和项目自己拼接 HTML 时用到的 tailwind 类，防止借 fixed/inset-0/z-50 等类做遮罩
const ALLOWED_CLASS = /^(language-[\w-]+|text-blue-500|cursor-pointer|inline-block|p-2|w-6|h-6|mx-0\.5|max-w-\[144px\]|max-h-40)$/;

const styleParser = document.createElement("div").style;

const isAllowedDeclaration = (tag: string, prop: string, value: string) => {
	if (!ALLOWED_STYLE_PROP.test(prop) || UNSAFE_STYLE_VALUE.test(value) || VIEWPORT_UNIT.test(value)) return false;
	return !SIZE_PROP.test(prop) || SIZED_TAGS.has(tag);
};

const filterAttribute: UponSanitizeAttributeHook = (node, data) => {
	if (data.attrName === "style") {
		// 交给浏览器解析 CSS，避免手写 split(";") 被引号里的分号绕过
		styleParser.cssText = data.attrValue;
		const kept: string[] = [];
		for (let i = 0; i < styleParser.length; i++) {
			const prop = styleParser[i];
			const value = styleParser.getPropertyValue(prop);
			if (isAllowedDeclaration(node.tagName, prop, value)) kept.push(`${prop}: ${value}`);
		}
		data.attrValue = kept.join("; ");
	} else if (data.attrName === "class") {
		data.attrValue = data.attrValue
			.split(/\s+/)
			.filter((c) => ALLOWED_CLASS.test(c))
			.join(" ");
	}
	if (!data.attrValue && (data.attrName === "style" || data.attrName === "class")) data.keepAttr = false;
};

const fixElement: NodeHook = (node) => {
	if (!(node instanceof Element)) return;
	const tag = node.tagName;
	if (tag === "A") {
		node.setAttribute("target", "_blank");
		node.setAttribute("rel", "noopener noreferrer");
	} else if (tag === "INPUT") {
		// 只允许只读复选框（待办），其余 input 一律移除
		if (node.getAttribute("type") !== "checkbox") return node.remove();
		node.setAttribute("disabled", "");
	} else if (tag === "IFRAME") {
		// 仅允许 https 第三方嵌入（视频等）；同源 iframe 配 allow-same-origin 会逃逸沙箱，直接移除
		let url: URL | null = null;
		try {
			url = new URL(node.getAttribute("src") || "", location.href);
		} catch {
			url = null;
		}
		if (!url || url.protocol !== "https:" || url.origin === location.origin) return node.remove();
		node.setAttribute("sandbox", "allow-scripts allow-same-origin allow-presentation");
		node.setAttribute("referrerpolicy", "no-referrer");
	}
};

/** 访客消息兜底：viewVisitorText 只会产出换行和表情图，这里同样只放行这两种，不允许链接 */
export const PLAIN_TEXT_CONFIG: Config = {
	ALLOWED_TAGS: ["br", "img"],
	ALLOWED_ATTR: ["class", "src", "alt"],
};

export const RICH_TEXT_HOOKS = {
	uponSanitizeAttribute: filterAttribute,
	afterSanitizeAttributes: fixElement,
};

const purifier = DOMPurify();
purifier.addHook("uponSanitizeAttribute", RICH_TEXT_HOOKS.uponSanitizeAttribute);
purifier.addHook("afterSanitizeAttributes", RICH_TEXT_HOOKS.afterSanitizeAttributes);

export const sanitizeRichText = (html: string) => purifier.sanitize(html, RICH_TEXT_CONFIG) as string;
